import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  FlatList,
  StyleSheet,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Product, Category } from '../types';
import MarcaModeloSelector from '../components/MarcaModeloSelector';
import { Marca, Modelo } from '../services/api';
import APIService from '../services/api';
import ProductCard from '../components/ProductCard';

interface SearchFilters {
  linea_id?: number; // Cambiado de categoria a linea_id
  modelo_id?: number; // Nuevo: filtro por modelo
  categoria?: string; // Mantenido para compatibilidad temporal
  minPrice?: number;
  maxPrice?: number;
  sortBy?: 'precio' | 'nombre' | 'created_at';
  sortOrder?: 'asc' | 'desc';
}

const SearchScreen: React.FC = (): React.ReactElement => {
  const navigation = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    sortBy: 'created_at',
    sortOrder: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);
  // Eliminar categorías/líneas
  const [usingBackend, setUsingBackend] = useState<boolean | null>(null);
  // Estado para Marca/Modelo
  const [selectedMarca, setSelectedMarca] = useState<Marca | undefined>(undefined);
  const [selectedModelo, setSelectedModelo] = useState<Modelo | undefined>(undefined);

  useEffect(() => {
    searchProducts();
  }, []);

  // Nuevo useEffect para reaccionar a cambios en filtros
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchProducts();
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [filters, searchQuery, selectedMarca, selectedModelo]);

  const loadCategories = async () => {
    try {
      console.log('🏷️ SOLO BACKEND: Cargando líneas del backend Laravel (reemplaza categorías)');
      
      const categoriesData = await APIService.getCategories();
      
      console.log('🏷️ SOLO BACKEND: Líneas recibidas:', categoriesData);
      console.log('🏷️ SOLO BACKEND: Número de líneas:', categoriesData?.length || 0);
      
      if (categoriesData && categoriesData.length > 0) {
        setCategories(categoriesData);
        setUsingBackend(true);
        console.log('✅ SOLO BACKEND: Líneas cargadas exitosamente');
      } else {
        console.log('⚠️ SOLO BACKEND: No se encontraron líneas en el backend');
        throw new Error('No se encontraron líneas en el backend');
      }
      
    } catch (error: any) {
      console.error('❌ SOLO BACKEND: Error cargando líneas:', error);
      console.error('❌ SOLO BACKEND: Error completo:', error.message);
      Alert.alert('Error', `No se pudieron cargar las líneas: ${error.message}`);
      setCategories([]);
      setUsingBackend(false);
    }
  };

  const searchProducts = async () => {
    setLoading(true);
    try {
      console.log('🔍 BÚSQUEDA INICIADA =============================');
      console.log('🔍 searchQuery:', searchQuery);
      console.log('🔍 searchQuery length:', searchQuery.length);
      console.log('🔍 searchQuery trimmed:', searchQuery.trim());
      console.log('🔍 Filtros completos:', JSON.stringify(filters, null, 2));
      
      const params: any = {};
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
        console.log('✅ Agregado parámetro search:', params.search);
      } else {
        console.log('⚠️ searchQuery vacío, no se agrega parámetro search');
      }
      
      // ACTUALIZADO: Usar linea_id en lugar de categoria
      // Filtrar solo por marca y modelo
      if (selectedMarca) {
        params.marca_id = selectedMarca.id;
        console.log('✅ Agregado parámetro marca_id:', params.marca_id);
      }
      if (selectedModelo) {
        params.modelo_id = selectedModelo.id;
        console.log('✅ Agregado parámetro modelo_id:', params.modelo_id);
      }
      // Compatibilidad temporal con categoria
      if (filters.categoria && !filters.linea_id) {
        params.categoria = filters.categoria;
        console.log('✅ Agregado parámetro categoria:', params.categoria);
      }
      if (filters.minPrice) {
        params.min_price = filters.minPrice;
        console.log('✅ Agregado parámetro min_price:', params.min_price);
      }
      if (filters.maxPrice) {
        params.max_price = filters.maxPrice;
        console.log('✅ Agregado parámetro max_price:', params.max_price);
      }
      if (filters.sortBy) {
        params.sort_by = filters.sortBy;
        params.sort_order = filters.sortOrder || 'asc';
        console.log('✅ Agregado parámetros sort:', params.sort_by, params.sort_order);
      }

      console.log('📦 PARÁMETROS FINALES A ENVIAR:', JSON.stringify(params, null, 2));
      
      const response = await APIService.getProducts(params);
      
      console.log('� RESPUESTA RECIBIDA:', response);
      console.log('� Productos encontrados:', response.data?.length || 0);
      if (response.data && response.data.length > 0) {
        console.log('🔍 Primeros 3 productos:', response.data.slice(0, 3).map((p: any) => ({ id: p.id, nombre: p.nombre })));
      }
      
      setProducts(response.data || []);
      setUsingBackend(true);
      console.log('🔍 BÚSQUEDA COMPLETADA ===========================');
      
    } catch (error: any) {
      console.error('❌ SOLO BACKEND: Error en búsqueda:', error);
      console.error('❌ SOLO BACKEND: Error completo:', error.message);
      Alert.alert('Error', `No se pudieron cargar los productos: ${error.message}`);
      setProducts([]);
      setUsingBackend(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    searchProducts();
  };

  const clearFilters = () => {
    setFilters({
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
    setSearchQuery('');
    setSelectedMarca(undefined);
    setSelectedModelo(undefined);
    searchProducts();
  };

  const renderProduct = ({ item }: { item: Product }): React.ReactElement => (
    <View style={styles.productContainer}>
      <ProductCard
        product={item}
        onPress={() => (navigation as any).navigate('ProductDetail', { productId: item.id })}
        onFavoritePress={() => {}}
      />
    </View>
  );

  // Eliminar renderCategoryFilter

  return (
    <SafeAreaView style={styles.container}>
      {/* Header con búsqueda */}
      <View style={styles.header}>
        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Buscar productos..."
            onSubmitEditing={handleSearch}
            returnKeyType="search"
          />
          <TouchableOpacity onPress={handleSearch} style={styles.searchButton}>
            <Text style={styles.searchButtonText}>🔍</Text>
          </TouchableOpacity>
        </View>
        
        <TouchableOpacity 
          onPress={() => setShowFilters(!showFilters)}
          style={styles.filterButton}
        >
          <Text style={styles.filterButtonText}>
            {showFilters ? '✕' : '⚙️'} Filtros
          </Text>
        </TouchableOpacity>
      </View>

      {/* Panel de filtros */}
      {showFilters && (
        <View style={styles.filtersContainer}>
          <View style={styles.filtersContent}>
            {/* Marca y Modelo */}
            <View style={styles.filterSection}>
              <Text style={styles.filterTitle}>Marca y Modelo:</Text>
              <MarcaModeloSelector
                selectedMarca={selectedMarca}
                selectedModelo={selectedModelo}
                onMarcaChange={(marca: Marca) => {
                  setSelectedMarca(marca);
                  setSelectedModelo(undefined);
                }}
                onModeloChange={(modelo: Modelo) => {
                  setSelectedModelo(modelo);
                }}
              />
            </View>

            {/* Botón de acción */}
            <View style={styles.filterActions}>
              <TouchableOpacity onPress={clearFilters} style={styles.clearButton}>
                <Text style={styles.clearButtonText}>Limpiar filtros</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Resultados */}
      <View style={styles.resultsContainer}>
        <Text style={styles.resultsCount}>
          {loading ? 'Buscando...' : `${products.length} producto${products.length !== 1 ? 's' : ''} encontrado${products.length !== 1 ? 's' : ''}`}
        </Text>

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#e74c3c" />
          </View>
        ) : (
          <FlatList
            data={products}
            renderItem={renderProduct}
            keyExtractor={(item: Product) => item.id.toString()}
            numColumns={2}
            contentContainerStyle={styles.productsList}
            showsVerticalScrollIndicator={false}
          />
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    backgroundColor: 'white',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  searchContainer: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  searchInput: {
    flex: 1,
    backgroundColor: '#F1F3F4',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    marginRight: 8,
  },
  searchButton: {
    backgroundColor: '#e74c3c',
    borderRadius: 8,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchButtonText: {
    fontSize: 18,
  },
  filterButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#F1F3F4',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  filtersContainer: {
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
    maxHeight: 200,
  },
  filtersContent: {
    padding: 16,
  },
  filterSection: {
    marginBottom: 20,
  },
  filterTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 12,
  },
  categoriesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryChip: {
    backgroundColor: '#F1F3F4',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  categoryChipActive: {
    backgroundColor: '#e74c3c',
  },
  categoryChipText: {
    fontSize: 12,
    color: '#333',
  },
  categoryChipTextActive: {
    color: 'white',
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  priceInput: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 16,
    minWidth: 100,
    textAlign: 'center',
  },
  priceSeparator: {
    fontSize: 18,
    color: '#666',
    fontWeight: '500',
    marginHorizontal: 8,
  },
  sortScrollContainer: {
    maxHeight: 50,
  },
  sortContainer: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 2,
    alignItems: 'center',
  },
  sortChip: {
    backgroundColor: '#F1F3F4',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    minWidth: 80,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  sortChipActive: {
    backgroundColor: '#e74c3c',
    shadowColor: '#e74c3c',
    shadowOpacity: 0.3,
  },
  sortChipText: {
    fontSize: 14,
    color: '#333',
    fontWeight: '500',
  },
  sortChipTextActive: {
    color: 'white',
    fontWeight: '600',
  },
  filterActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e1e8ed',
  },
  clearButton: {
    flex: 1,
    backgroundColor: '#95a5a6',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  clearButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  applyButton: {
    flex: 1,
    backgroundColor: '#e74c3c',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  applyButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  resultsContainer: {
    flex: 1,
    padding: 16,
  },
  resultsCount: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    fontWeight: '500',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  productsList: {
    paddingBottom: 20,
  },
  productContainer: {
    flex: 1,
    marginHorizontal: 4,
    marginBottom: 16,
  },
});

export default SearchScreen;
